import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Typography, Button, Space, Radio, Tag, Empty, message, Tooltip, Spin,
} from 'antd';
import { LeftOutlined, RightOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchCalendar, updateFollowup } from '../services/callService';
import { fetchSettings } from '../services/settingsService';
import { eventColor, colorMap, TYPE_COLORS, BRAND } from './callConstants';
import FollowupDetailDrawer from './FollowupDetailDrawer';

const { Title, Text } = Typography;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CallCalendar = () => {
  const navigate = useNavigate();
  const [viewType, setViewType] = useState('month');
  const [cursor, setCursor] = useState(dayjs());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeColors, setTypeColors] = useState(TYPE_COLORS);
  const [detail, setDetail] = useState(null);
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchSettings();
        setTypeColors({ ...TYPE_COLORS, ...colorMap(s.followup_type) });
      } catch { /* ignore */ }
    })();
  }, []);

  // Visible range depends on the active view
  const range = useMemo(() => {
    if (viewType === 'day') return [cursor.startOf('day'), cursor.endOf('day')];
    if (viewType === 'week') return [cursor.startOf('week'), cursor.endOf('week')];
    if (viewType === 'agenda') return [cursor.startOf('day'), cursor.add(60, 'day').endOf('day')];
    return [cursor.startOf('month').startOf('week'), cursor.endOf('month').endOf('week')];
  }, [viewType, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCalendar(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'));
      setEvents(data);
    } catch { message.error('Failed to load calendar'); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.EventAt) continue;
      const k = dayjs(e.EventAt).format('YYYY-MM-DD');
      (map[k] = map[k] || []).push(e);
    }
    Object.values(map).forEach(list => list.sort((a, b) => dayjs(a.EventAt) - dayjs(b.EventAt)));
    return map;
  }, [events]);

  const move = (dir) => {
    const unit = viewType === 'agenda' ? 'month' : viewType;
    setCursor(c => dir === 0 ? dayjs() : c.add(dir, unit === 'week' ? 'week' : unit));
  };

  const onDropDay = async (dayKey) => {
    if (!dragId) return;
    const ev = events.find(e => e.FollowupID === dragId);
    setDragId(null);
    if (!ev) return;
    const oldT = dayjs(ev.EventAt);
    const newAt = dayjs(dayKey).hour(oldT.hour()).minute(oldT.minute()).second(0);
    if (newAt.format('YYYY-MM-DD') === oldT.format('YYYY-MM-DD')) return;
    try {
      await updateFollowup(ev.FollowupID, { ScheduledAt: newAt.format('YYYY-MM-DD HH:mm:ss') });
      message.success(`Moved to ${newAt.format('DD MMM')}`);
      load();
    } catch { message.error('Failed to reschedule'); }
  };

  const EventChip = ({ ev, showTime = true }) => (
    <div
      draggable
      onDragStart={() => setDragId(ev.FollowupID)}
      onClick={(e) => { e.stopPropagation(); setDetail(ev); }}
      title={`${ev.CustomerName || ''} · ${ev.FollowupType || ''}`}
      style={{
        background: eventColor(ev, typeColors), color: '#fff', borderRadius: 4,
        padding: '1px 6px', fontSize: 11, marginBottom: 2, cursor: 'pointer',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        opacity: ev.Status === 'Completed' ? 0.7 : 1,
      }}
    >
      {showTime && dayjs(ev.EventAt).format('HH:mm')} {ev.CustomerName || ev.FollowupType}
    </div>
  );

  // ---------- Month ----------
  const monthView = () => {
    const days = [];
    let d = range[0];
    while (d.isBefore(range[1])) { days.push(d); d = d.add(1, 'day'); }
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WEEKDAYS.map(w => (
            <div key={w} style={{ textAlign: 'center', padding: '6px 0', fontWeight: 600, color: '#8c8c8c' }}>{w}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
          {days.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const inMonth = day.month() === cursor.month();
            const isToday = day.isSame(dayjs(), 'day');
            const list = eventsByDay[key] || [];
            return (
              <div key={key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropDay(key)}
                onClick={() => { setViewType('day'); setCursor(day); }}
                style={{
                  minHeight: 104, borderRight: '1px solid #f5f5f5', borderBottom: '1px solid #f5f5f5',
                  padding: 4, background: inMonth ? '#fff' : '#fafafa', cursor: 'pointer',
                }}>
                <div style={{ textAlign: 'right', padding: '0 4px' }}>
                  <span style={{
                    display: 'inline-block', minWidth: 22, height: 22, lineHeight: '22px', textAlign: 'center',
                    borderRadius: '50%', fontSize: 12,
                    background: isToday ? BRAND : 'transparent', color: isToday ? '#fff' : (inMonth ? '#000' : '#bfbfbf'),
                  }}>{day.date()}</span>
                </div>
                {list.slice(0, 3).map(ev => <EventChip key={ev.FollowupID} ev={ev} />)}
                {list.length > 3 && <Text type="secondary" style={{ fontSize: 11 }}>+{list.length - 3} more</Text>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------- Week ----------
  const weekView = () => {
    const days = [];
    let d = range[0];
    while (d.isBefore(range[1])) { days.push(d); d = d.add(1, 'day'); }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {days.map(day => {
          const key = day.format('YYYY-MM-DD');
          const list = eventsByDay[key] || [];
          const isToday = day.isSame(dayjs(), 'day');
          return (
            <div key={key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropDay(key)}
              style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, minHeight: 200, background: isToday ? '#f6ffed' : '#fff' }}>
              <div style={{ textAlign: 'center', marginBottom: 8, fontWeight: 600 }}>
                {day.format('ddd')}<br /><span style={{ color: isToday ? BRAND : '#000' }}>{day.format('DD')}</span>
              </div>
              {list.length ? list.map(ev => <EventChip key={ev.FollowupID} ev={ev} />) : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>}
            </div>
          );
        })}
      </div>
    );
  };

  // ---------- Day ----------
  const dayView = () => {
    const key = cursor.format('YYYY-MM-DD');
    const list = eventsByDay[key] || [];
    return (
      <div>
        {list.length === 0 ? <Empty description={`No events on ${cursor.format('DD MMM YYYY')}`} /> : list.map(ev => (
          <div key={ev.FollowupID} onClick={() => setDetail(ev)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8, cursor: 'pointer' }}>
            <div style={{ width: 6, height: 40, borderRadius: 3, background: eventColor(ev, typeColors) }} />
            <div style={{ width: 80 }}><Text strong>{dayjs(ev.EventAt).format('hh:mm A')}</Text></div>
            <div style={{ flex: 1 }}>
              <Text strong>{ev.CustomerName}</Text>{' '}
              {ev.FollowupType && <Tag color={typeColors[ev.FollowupType] || 'blue'}>{ev.FollowupType}</Tag>}
              <div><Text type="secondary" style={{ fontSize: 12 }}>{ev.CustomerMobile} · {ev.City || ''} · {ev.ExecutiveName || ''}</Text></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ---------- Agenda ----------
  const agendaView = () => {
    const keys = Object.keys(eventsByDay).sort();
    if (!keys.length) return <Empty description="No upcoming events" />;
    return keys.map(key => (
      <div key={key} style={{ marginBottom: 16 }}>
        <Text strong style={{ color: BRAND }}>{dayjs(key).format('dddd, DD MMM YYYY')}</Text>
        <div style={{ marginTop: 8 }}>
          {eventsByDay[key].map(ev => (
            <div key={ev.FollowupID} onClick={() => setDetail(ev)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 6, cursor: 'pointer' }}>
              <div style={{ width: 6, height: 30, borderRadius: 3, background: eventColor(ev, typeColors) }} />
              <Text style={{ width: 80 }}>{dayjs(ev.EventAt).format('hh:mm A')}</Text>
              <Text strong>{ev.CustomerName}</Text>
              {ev.FollowupType && <Tag color={typeColors[ev.FollowupType] || 'blue'}>{ev.FollowupType}</Tag>}
            </div>
          ))}
        </div>
      </div>
    ));
  };

  const label = viewType === 'month' ? cursor.format('MMMM YYYY')
    : viewType === 'week' ? `${range[0].format('DD MMM')} – ${range[1].format('DD MMM YYYY')}`
    : viewType === 'day' ? cursor.format('dddd, DD MMM YYYY')
    : 'Next 60 days';

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }} gutter={[8, 8]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Calendar</Title>
          <Text type="secondary">Calls, visits, quotations &amp; reminders</Text>
        </Col>
        <Col>
          <Space wrap>
            <Button onClick={() => move(0)}>Today</Button>
            <Button icon={<LeftOutlined />} onClick={() => move(-1)} />
            <Button icon={<RightOutlined />} onClick={() => move(1)} />
            <Text strong style={{ minWidth: 160, textAlign: 'center' }}>{label}</Text>
            <Radio.Group value={viewType} onChange={(e) => setViewType(e.target.value)} optionType="button" buttonStyle="solid"
              options={[{ label: 'Month', value: 'month' }, { label: 'Week', value: 'week' }, { label: 'Day', value: 'day' }, { label: 'Agenda', value: 'agenda' }]} />
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} /></Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/calls/new')}>New</Button>
          </Space>
        </Col>
      </Row>

      {/* Legend */}
      <Space wrap style={{ marginBottom: 12, padding: '0 8px' }}>
        {Object.entries(typeColors).map(([k, c]) => (
          <span key={k} style={{ fontSize: 12 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c, marginRight: 4 }} />{k}
          </span>
        ))}
        <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f5222d', marginRight: 4 }} />Missed</span>
        <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#bfbfbf', marginRight: 4 }} />Completed</span>
      </Space>

      <Card bordered={false} style={{ borderRadius: 8 }} bodyStyle={{ padding: 16 }}>
        {loading ? <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div> : (
          viewType === 'month' ? monthView()
            : viewType === 'week' ? weekView()
            : viewType === 'day' ? dayView()
            : agendaView()
        )}
      </Card>

      <FollowupDetailDrawer open={!!detail} followup={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
};

export default CallCalendar;
